import React, { useMemo } from 'react';

function scoreCO2(val: number): number {
  if (val <= 600) return 100;
  if (val <= 1000) return 80;
  if (val <= 1500) return 60;
  if (val <= 2000) return 40;
  return 20;
}
function scoreTVOC(val: number): number {
  if (val <= 0.1 || val <= 100) return 100;
  if (val <= 0.3 || val <= 250) return 80;
  if (val <= 0.6 || val <= 400) return 60;
  if (val <= 1.0 || val <= 600) return 40;
  return 20;
}
function scorePM25(val: number): number {
  if (val <= 15) return 100;
  if (val <= 35) return 80;
  if (val <= 75) return 50;
  return 20;
}
function getAQIGrade(score: number) {
  if (score >= 80) return { label: '좋음', color: 'text-emerald-400', bgColor: 'bg-emerald-500/20' };
  if (score >= 60) return { label: '보통', color: 'text-cyan-400', bgColor: 'bg-cyan-500/20' };
  if (score >= 40) return { label: '나쁨', color: 'text-amber-400', bgColor: 'bg-amber-500/20' };
  return { label: '매우나쁨', color: 'text-red-400', bgColor: 'bg-red-500/20' };
}
function getComfortScoreLabel(temp: number, humidity: number) {
  const tempOk = temp >= 20 && temp <= 26;
  const humOk = humidity >= 40 && humidity <= 60;
  if (tempOk && humOk) return { label: '최적', color: 'text-emerald-400' };
  if (tempOk || humOk) return { label: '보통', color: 'text-cyan-400' };
  return { label: '주의', color: 'text-amber-400' };
}
function getEnergyGrade(kwh: number) {
  if (kwh <= 5) return { label: '절약', color: 'text-emerald-400' };
  if (kwh <= 15) return { label: '적정', color: 'text-cyan-400' };
  if (kwh <= 25) return { label: '보통', color: 'text-amber-400' };
  return { label: '높음', color: 'text-red-400' };
}

import { motion } from 'framer-motion';
import { SparklesIcon, CpuIcon, LeafIcon, ThermometerIcon, DropletsIcon, WindIcon, FlaskConicalIcon, ZapIcon, PersonStandingIcon, LightbulbIcon } from 'lucide-react';
import { useTelemetry } from '../../hooks/useTelemetry';
import { useTelemetryHistory } from '../../hooks/useHistoricalTelemetry';
import { whToKwh } from '../../utils/energyUtils';
import { useEditorZones } from '../../hooks/useEditorZones';
import { useTranslation } from 'react-i18next';
import { EnergyChart } from './EnergyChart';
import { MonthlyEnergyWidget } from './MonthlyEnergyWidget';
import { TopOutletWidget } from './TopOutletWidget';
import { OutdoorEnvironmentWidget } from './OutdoorEnvironmentWidget';
import { ZoneDeviceStatusWidget } from './ZoneDeviceStatusWidget';
import { SensorListView, Sensor } from './SensorListView';

interface AllSpacesViewProps {
  apartmentAssetId: string | null;
  homeName: string | null;
  seoSensors?: any[];
  allSensors?: Sensor[];
  selectedRoomId?: string;
  rooms?: any[];
  telemetry?: Record<string, any>;
  sensorTelemetry?: Record<string, any>;
  onSelectRoom?: (room: any) => void;
  onSelectSensor?: (sensor: Sensor) => void;
  onShowToast?: (message: string, type?: 'success' | 'info' | 'error') => void;
}

export function AllSpacesView({ apartmentAssetId, homeName, seoSensors = [], allSensors = [], selectedRoomId = 'ALL', rooms = [], telemetry = {}, sensorTelemetry = {}, onSelectRoom, onSelectSensor, onShowToast }: AllSpacesViewProps) {
  const { t } = useTranslation();
  
  // Fetch zone hierarchy to identify primary zone and its sensors
  const { zones: rawZones, primaryZoneId } = useEditorZones(apartmentAssetId);

  // Strictly filter zones to only permitted rooms passed from MobileApp
  const zones = useMemo(() => {
    if (!rooms || rooms.length === 0) return rawZones;
    const permittedRoomIds = new Set(rooms.map(r => r.id));
    if (permittedRoomIds.has('ALL')) return rawZones;
    return rawZones.filter(z => permittedRoomIds.has(z.id));
  }, [rawZones, rooms]);

  // Find selected room / zone
  const currentSelectedRoom = rooms.find(r => r.id === selectedRoomId);
  const isSpecificZone = selectedRoomId && selectedRoomId !== 'ALL';

  // Identify the primary/selected zone and its SES environment sensor
  const primaryZone = zones.find(z => z.id === primaryZoneId) || zones.find(z => z.name === '거실') || zones[0];
  const selectedZone = isSpecificZone ? (zones.find(z => z.id === selectedRoomId) || zones.find(z => z.name === currentSelectedRoom?.name) || primaryZone) : primaryZone;
  
  // Find SES sensor for the selected zone
  const zoneEnvSensor = allSensors.find(s => isSpecificZone && (s.roomId === selectedRoomId || s.roomId === selectedZone?.id) && s.type === 'SES')
    || selectedZone?.sensors?.find((s: any) => s.type === 'SES')
    || allSensors.find(s => s.type === 'SES');

  // Real-time telemetry for selected zone's SES sensor (or fallback to asset)
  const targetId = zoneEnvSensor?.id || selectedZone?.id || apartmentAssetId;
  const targetType = zoneEnvSensor ? 'DEVICE' : 'ASSET';

  const { data: zoneTelemetry } = useTelemetry(
    targetId,
    ['temperature', 'humidity', 'dust', 'pm25', 'pm2_5', 'PM25', 'TVOCs', 'CO2', 'presence_state'],
    targetType
  );
  const { data: energyHistory } = useTelemetryHistory(
    apartmentAssetId || '',
    ['daily_power'],
    'monthly',
    'ASSET'
  );

  // Extract values or fallback
  const temp = zoneTelemetry['temperature']?.[0]?.value ? Number(zoneTelemetry['temperature'][0].value) : 0;
  const humidity = zoneTelemetry['humidity']?.[0]?.value ? Number(zoneTelemetry['humidity'][0].value) : 0;
  const aqi = zoneTelemetry['dust']?.[0]?.value ? Number(zoneTelemetry['dust'][0].value) : zoneTelemetry['pm25']?.[0]?.value ? Number(zoneTelemetry['pm25'][0].value) : zoneTelemetry['pm2_5']?.[0]?.value ? Number(zoneTelemetry['pm2_5'][0].value) : 0;
  const tvoc = zoneTelemetry['TVOCs']?.[0]?.value ? Number(zoneTelemetry['TVOCs'][0].value) : 0;
  const co2 = zoneTelemetry['CO2']?.[0]?.value ? Number(zoneTelemetry['CO2'][0].value) : 0;

  // AQI status
  const aqiStatus = aqi === 0 ? '-' : aqi <= 50 ? t('sidebar.aqi_good') : aqi <= 100 ? t('sidebar.aqi_fair') : t('sidebar.aqi_bad');
  const aqiColor = aqi === 0 ? 'text-slate-500' : aqi <= 50 ? 'text-emerald-400' : aqi <= 100 ? 'text-amber-400' : 'text-red-400';

  // Humidity status
  const humidityOk = humidity >= 30 && humidity <= 60;
  const humidityColor = humidity === 0 ? 'text-slate-500' : humidityOk ? 'text-cyan-400' : 'text-amber-400';

  // TVOC status
  const tvocStatus = tvoc === 0 ? '-' : tvoc < 200 ? t('sidebar.tvoc_good') : tvoc < 400 ? t('sidebar.tvoc_fair') : t('sidebar.tvoc_bad');
  const tvocColor = tvoc === 0 ? 'text-slate-500' : tvoc < 200 ? 'text-emerald-400' : tvoc < 400 ? 'text-amber-400' : 'text-red-400';

    return (
    <div className="w-full h-full overflow-y-auto pb-20 px-4 pt-4 hide-scrollbar flex flex-col gap-4">
      {/* 0. Outdoor Environment Section */}
      <OutdoorEnvironmentWidget assetId={apartmentAssetId} />

      {/* 1. 통합 실내환경 & 에너지 요약 3종 카드 */}
      <div className="flex flex-col gap-2">
        {/* Header row (Hidden as requested) */}
        {false && (
        <div className="flex items-center justify-between mb-0.5 px-1">
          <div className="flex items-center gap-2">
            <LeafIcon className="w-4 h-4 text-emerald-400" />
            <span className="text-[1.05rem] font-medium tracking-tight text-[var(--mobile-text-primary)]">
              {t('sidebar.indoor_env')}
            </span>
          </div>
          <span className="text-[0.7rem] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-medium">
            {isSpecificZone && currentSelectedRoom ? currentSelectedRoom.name : (primaryZone?.name || '거실')} 기준
          </span>
        </div>
        )}

        {/* 3 Summary Cards: 쾌적성 | 재실 & 조명 | 공기질 */}
        {(() => {
          const comfort = getComfortScoreLabel(temp, humidity);
          const co2Score = scoreCO2(co2 || 450);
          const tvocScore = scoreTVOC(tvoc || 0);
          const pm25Score = scorePM25(aqi || 10);
          const overallScore = Math.round((co2Score + tvocScore + pm25Score) / 3);
          const aqiInfo = getAQIGrade(overallScore);

          // Calculate occupancy (재실 여부) & active presence count (재실감지센서 카운트)
          let presenceSensorCount = 0;
          let isOccupied = false;

          if (isSpecificZone && currentSelectedRoom) {
            // 특정 공간 선택 시
            const targetSensors = allSensors.filter(s => s.roomId === selectedRoomId || s.roomId === selectedZone?.id);
            const radarSensors = targetSensors.filter(s => s.type === 'RADAR');
            
            const zonePresenceVal = telemetry?.[selectedZone?.id]?.presence_state?.[0]?.value ?? zoneTelemetry?.['presence_state']?.[0]?.value;
            const isZoneActive = zonePresenceVal === '1' || zonePresenceVal === 1 || zonePresenceVal === 'true' || zonePresenceVal === true;

            const activeSensorsCount = radarSensors.filter(s => {
              const val = sensorTelemetry[s.id]?.presence_state?.[0]?.value;
              return val === '1' || val === 1 || val === 'true' || val === true;
            }).length;

            presenceSensorCount = activeSensorsCount > 0 ? activeSensorsCount : (isZoneActive ? 1 : 0);
            isOccupied = presenceSensorCount > 0;
          } else {
            // 전체 공간 (호) 선택 시: 모든 zone 및 radar 센서의 presence_state 검사
            const activeZoneCount = zones.filter(z => {
              const val = telemetry?.[z.id]?.presence_state?.[0]?.value;
              return val === '1' || val === 1 || val === 'true' || val === true;
            }).length;

            const radarSensors = allSensors.filter(s => s.type === 'RADAR');
            const activeSensorsCount = radarSensors.filter(s => {
              const val = sensorTelemetry[s.id]?.presence_state?.[0]?.value;
              return val === '1' || val === 1 || val === 'true' || val === true;
            }).length;

            const hoPresenceVal = telemetry?.[apartmentAssetId || '']?.presence_state?.[0]?.value;
            const isHoActive = hoPresenceVal === '1' || hoPresenceVal === 1 || hoPresenceVal === 'true' || hoPresenceVal === true;

            presenceSensorCount = Math.max(activeZoneCount, activeSensorsCount, isHoActive ? 1 : 0);
            isOccupied = presenceSensorCount > 0;
          }

          return (
            <div className="w-full flex flex-col">
              {/* Section Header: 실내환경 */}
              <div className="flex items-center gap-2 mb-2 px-1 text-[var(--mobile-text-muted)]">
                <SparklesIcon className="w-4 h-4 text-cyan-400" />
                <span className="text-[1rem] text-[var(--mobile-text-primary)] font-medium uppercase tracking-wider">
                  실내환경
                </span>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="grid grid-cols-3 gap-2"
              >
                {/* 1. 온도 */}
                <div className="py-3 px-2 rounded-2xl border bg-[var(--mobile-bg-card)] border-[var(--mobile-border)] shadow-sm flex flex-col items-center justify-center gap-1 min-h-[76px]">
                  {/* 1줄: 아이콘 + 제목 */}
                  <div className="flex items-center gap-1.5">
                    <ThermometerIcon className="w-3.5 h-3.5 text-rose-400" />
                    <span className="text-[0.8rem] font-bold tracking-tight text-[var(--mobile-text-muted)]">
                      온도
                    </span>
                  </div>
                  {/* 2줄: 수치 */}
                  <div className="flex items-baseline gap-0.5">
                    <span className="text-xl font-mono font-bold text-rose-400">
                      {temp > 0 ? temp.toFixed(1) : '-'}
                    </span>
                    <span className="text-xs font-mono text-[var(--mobile-text-muted)]">°C</span>
                  </div>
                </div>

                {/* 2. 습도 */}
                <div className="py-3 px-2 rounded-2xl border bg-[var(--mobile-bg-card)] border-[var(--mobile-border)] shadow-sm flex flex-col items-center justify-center gap-1 min-h-[76px]">
                  {/* 1줄: 아이콘 + 제목 */}
                  <div className="flex items-center gap-1.5">
                    <DropletsIcon className="w-3.5 h-3.5 text-cyan-400" />
                    <span className="text-[0.8rem] font-bold tracking-tight text-[var(--mobile-text-muted)]">
                      습도
                    </span>
                  </div>
                  {/* 2줄: 수치 */}
                  <div className="flex items-baseline gap-0.5">
                    <span className="text-xl font-mono font-bold text-cyan-400">
                      {humidity > 0 ? humidity.toFixed(1) : '-'}
                    </span>
                    <span className="text-xs font-mono text-[var(--mobile-text-muted)]">%</span>
                  </div>
                </div>

                {/* 3. 미세먼지 */}
                <div className="py-3 px-2 rounded-2xl border bg-[var(--mobile-bg-card)] border-[var(--mobile-border)] shadow-sm flex flex-col items-center justify-center gap-1 min-h-[76px]">
                  {/* 1줄: 아이콘 + 제목 */}
                  <div className="flex items-center gap-1.5">
                    <WindIcon className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-[0.8rem] font-bold tracking-tight text-[var(--mobile-text-muted)]">
                      미세먼지
                    </span>
                  </div>
                  {/* 2줄: 수치 */}
                  <div className="flex items-baseline gap-0.5">
                    <span className="text-xl font-mono font-bold text-emerald-400">
                      {aqi > 0 ? aqi.toFixed(0) : '-'}
                    </span>
                    <span className="text-xs font-mono text-[var(--mobile-text-muted)]">㎍</span>
                  </div>
                </div>
              </motion.div>
            </div>
          );
        })()}
      </div>

      {/* 2. Today's Electric Usage Chart (Hidden) */}
      {false && <EnergyChart currentAssetId={apartmentAssetId} />}

      {/* 2.5 Zone Lighting & SEM Device Status Widget (SED 상태) */}
      <div className="w-full flex flex-col">
        {/* Section Header: SED 상태 */}
        <div className="flex items-center gap-2 mb-2 px-1 text-[var(--mobile-text-muted)]">
          <CpuIcon className="w-4 h-4 text-emerald-400" />
          <span className="text-[1rem] text-[var(--mobile-text-primary)] font-medium uppercase tracking-wider">
            SED 상태
          </span>
        </div>

        <ZoneDeviceStatusWidget
          sensors={
            isSpecificZone && currentSelectedRoom
              ? allSensors.filter((s) => s.roomId === selectedRoomId || s.roomId === selectedZone?.id || s.roomName === currentSelectedRoom?.name)
              : allSensors
          }
          sensorTelemetry={sensorTelemetry}
          selectedRoomId={selectedRoomId}
          roomName={currentSelectedRoom?.name || '전체공간'}
        />
      </div>

      {/* 3. Monthly Electric Usage (Scoped by selected Zone or whole Apartment) */}
      <div className="w-full">
        <MonthlyEnergyWidget
          key={isSpecificZone ? (selectedZone?.id || currentSelectedRoom?.id) : (apartmentAssetId || 'all')}
          currentAssetId={
            isSpecificZone && (selectedZone?.id || currentSelectedRoom?.id)
              ? (selectedZone?.id || currentSelectedRoom?.id)
              : apartmentAssetId
          }
        />
      </div>

      {/* 4. Top Outlets (Scoped by selected Zone or whole Apartment) */}
      <div className="w-full">
        <TopOutletWidget
          seoSensors={
            isSpecificZone
              ? seoSensors.filter(
                  (s) =>
                    s.roomId === selectedRoomId ||
                    s.roomId === selectedZone?.id ||
                    s.roomName === currentSelectedRoom?.name
                )
              : seoSensors
          }
          sensorTelemetry={sensorTelemetry}
        />
      </div>



    </div>
  );
}
